/*
Author: Pooja Shenoy
Story: ENT-2317
 */
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef
} from '@angular/core';
import {ConstantsService} from '../../../../services/constants/constants.service';
import {BsModalService} from "ngx-bootstrap/modal";
import {WarningModalContentComponent} from "../../../../lib/warning-modal/warning-modal.component";
import { OrganisationService } from "../../../../services/organisation/organisation.service";
import {RequestService} from "../../../../services/request/request.service";
import {PermissionsService} from "../../../../services/permissions/permissions.service";

@Component({
  selector: 'app-request-settings',
  templateUrl: './request-settings.component.pug',
  styleUrls: ['./request-settings.component.scss'],
  standalone: false
})
export class RequestSettingsComponent implements OnInit, OnChanges {
  @Input() org: any;
  @Input() defaultPayload: any;
  @Input() defaultNotes: any;
  @Input() isViewMode: any = false;
  @Input() templateMode: any;
  @Output() defaultPayloadChange = new EventEmitter();
  @Output() requestSettingsError = new EventEmitter();
  @ViewChild('input')
  inputRef!: ElementRef;

  loading = false;
  defaultRequestSettings=ConstantsService.DEFAULT_REQUEST_SETTINGS;

  //default values set
  notesToCandidateCharLimit = 500;
  notesToRefereeCharLimit = 500;
  companyName = '';

  //errors
  referenceCountNaNError = false;
  referenceBelowExpectedCountError = false;
  referenceAboveExpectedCountError = false;
  referenceIncrementBtnDisabled = false;
  referenceDecrementBtnDisabled = false;
  referenceInputError = false;
  noteToCandidateEmpty = false;
  noteToRefereeEmpty = false;
  errorFeedback = true;
  errors = [
    {name: 'lessThan1', msg: $localize`Reference count cannot be less than 1`, enabled: false},
    {name: 'moreThan5', msg: $localize`Minimum number of references cannot be greater than 5`, enabled: false},
  ];

  coverageBelowExpectedCountError = false;
  coverageAboveExpectedCountError = false;
  coverageIncrementBtnDisabled = false;
  coverageDecrementBtnDisabled = false;
  coverageInputError = false;

  allowTimeGapsWeeksBelowCountError = false;
  allowTimeGapsWeeksAboveCountError = false;
  allowTimeGapsDaysBelowCountError = false;
  allowTimeGapsDaysAboveCountError = false;
  allowTimeGapIncrementBtnDisabled = false;
  allowTimeGapDecrementBtnDisabled = false;
  allowTimeGapInputError = false;

  //btn clicks
  revertToDefaultRefereeNoteClicked = false;
  revertToDefaultCandidateNoteClicked = false;
  minimumCoverageClicked = false;

  revertToDefaultRefereeNoteDisabled = true;
  revertToDefaultCandidateNoteDisabled = true;

  //tooltip texts
  noteToCandidateeTooltip = $localize`This customisable message will appear in the email to the candidate and when they nominate their referees.`
  noteToRefereeTooltip = $localize`This customisable message will appear in the email to the referee and when they provide their feedback.`
  sendOnBehalfOfTooltip = $localize`Used when collecting references on behalf of another entity. The selected entity will be shown to the candidate in all correspondence.`;
  allowTimeGapsTooltip = $localize`Allow for time gaps that do not need to be accounted for by the candidate.`;
  minCoveragePeriodTooltip = $localize`By selecting a minimum coverage period, candidates must provide details of their activities during this time, up to and including the day the request is sent.`;
  hiringAccounts: Array<any> = [];
  selectedHiringAccount = "";

  requestTemplate = false;

  private bsModalRef: any;

  template = {}

  constructor(private modalService: BsModalService,
              private requestService: RequestService,
              private permissions: PermissionsService
             ) { }

  ngOnInit(): void {
    this.requestTemplate = this.permissions.isFeatureActivated('ent-10605-request-templates');
    this.getHiringAccounts();

    this.noteToCandidateEmpty = this.defaultPayload.noteToCandidate == '';
    this.noteToRefereeEmpty = this.defaultPayload.noteToReferee == '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if(!changes.defaultPayload){
      if (this.org.uid) {
        this.companyName = this.org['brand_name'];
        let orgName = {
          "id": 0,
          "name": this.companyName
        }
        this.selectedHiringAccount = this.companyName;
        this.hiringAccounts.splice(0,0,orgName);
      }
    }
    if(changes.defaultPayload?.previousValue !== undefined ){
      this.updateReferenceCount(
          {target:{value:changes.defaultPayload.currentValue.referenceCount}})
      this.updateCoverageCount(
          {target:{value:changes.defaultPayload.currentValue.coverageCount}})
      this.updateTimeGapCount(
          {target:{value:changes.defaultPayload.currentValue.timeGap}})
    }

    if(changes.defaultPayload && changes.defaultPayload.currentValue) {
      this.noteToCandidateEmpty = changes.defaultPayload.currentValue.noteToCandidate == '';
      this.noteToRefereeEmpty = changes.defaultPayload.currentValue.noteToReferee == '';

      this.revertToDefaultCandidateNoteDisabled = changes.defaultPayload.currentValue.noteToCandidate == this.defaultNotes.noteToCandidate;
      this.revertToDefaultRefereeNoteDisabled = changes.defaultPayload.currentValue.noteToReferee == this.defaultNotes.noteToReferee;
    }
  }

  setError() {
    this.requestSettingsError.emit(this.referenceInputError || this.coverageInputError || this.allowTimeGapInputError);
  }

  // Reference count

  resetReferenceCountErrors() {
    this.referenceDecrementBtnDisabled = false;
    this.referenceIncrementBtnDisabled = false;
    this.referenceAboveExpectedCountError = false;
    this.referenceBelowExpectedCountError = false;
    this.referenceCountNaNError = false;
  }
  updateReferenceCount(event) {

    this.resetReferenceCountErrors();
    let referenceCount: number;

    referenceCount = parseInt(typeof event === 'string' ? event : event?.target?.value);


    if (isNaN(referenceCount)) { //check if reference count is not a number
      this.referenceCountNaNError = true;
    }

    if(!this.referenceCountNaNError) {
      if (referenceCount < this.defaultRequestSettings.minReferenceCount) {
        this.referenceBelowExpectedCountError = true;
        this.referenceIncrementBtnDisabled = true;
        this.referenceDecrementBtnDisabled = true;
      } else if (referenceCount > this.defaultRequestSettings.maxReferenceCount) {
        this.referenceAboveExpectedCountError = true;
        this.referenceIncrementBtnDisabled = true;
        this.referenceDecrementBtnDisabled = true;
      } else { //edge case when user enters max/min number
        this.referenceIncrementBtnDisabled = (referenceCount == this.defaultRequestSettings.maxReferenceCount) ? true : false;
        this.referenceDecrementBtnDisabled = (referenceCount == this.defaultRequestSettings.minReferenceCount) ? true : false;
      }
    }
    this.defaultPayload["referenceCount"] = referenceCount;
    this.defaultPayloadChange.emit(this.defaultPayload);
    this.referenceInputError = this.referenceBelowExpectedCountError || this.referenceAboveExpectedCountError || this.noteToRefereeEmpty || this.noteToCandidateEmpty || this.referenceCountNaNError;

    this.errors.forEach(x => x.enabled = false);
    if(this.referenceBelowExpectedCountError){
      this.errors.find(o => o.name === 'lessThan1')!.enabled = true;
    } else if (this.referenceAboveExpectedCountError){
      this.errors.find(o => o.name === 'moreThan5')!.enabled = true;
    }
    this.errors = [...this.errors];
    this.setError();
  }

  maxReferenceCountPopUpDisplay(){
    this.bsModalRef = this.modalService.show(WarningModalContentComponent, {
      class: 'modal-dialog-centered',
      initialState: {
        "title": $localize`Minimum references`,
        "message": $localize`By increasing the minimum number of references, candidates will not be able to complete the process if they are unable to provide the required number of references.\n\nAre you sure you want to continue?`
        }
    });
    this.bsModalRef.content.selection.subscribe(res => {
        if (res === true){
          this.defaultPayload["referenceCount"] += 1;
          this.referenceDecrementBtnDisabled = ( this.defaultPayload["referenceCount"] < this.defaultRequestSettings.minReferenceCount) ? true : false;
          this.referenceIncrementBtnDisabled = ( this.defaultPayload["referenceCount"] > this.defaultRequestSettings.maxReferenceCount - 1) ? true : false; //subtracting -1 as it btn needs to be disabled at the max count
          this.defaultPayloadChange.emit(this.defaultPayload);
        }
    });
  }


  incrementReferenceCount() {
    if(this.defaultPayload["referenceCount"]==4){
      this.maxReferenceCountPopUpDisplay();
    } else{
      this.referenceDecrementBtnDisabled = ( this.defaultPayload["referenceCount"]+1 < this.defaultRequestSettings.minReferenceCount) ? true : false;
      this.referenceIncrementBtnDisabled = ( this.defaultPayload["referenceCount"]+1 > this.defaultRequestSettings.maxReferenceCount - 1) ? true : false; //subtracting -1 as it btn needs to be disabled at the max count
      this.defaultPayload["referenceCount"] += 1;
      this.defaultPayloadChange.emit(this.defaultPayload);
    }
  }

  decrementReferenceCount() {
    this.referenceDecrementBtnDisabled = (this.defaultPayload["referenceCount"]-1 < this.defaultRequestSettings.minReferenceCount + 1) ? true : false; //adding +1 as it btn needs to be disabled at the min count
    this.referenceIncrementBtnDisabled = (this.defaultPayload["referenceCount"]-1 > this.defaultRequestSettings.maxReferenceCount) ? true : false;
    this.defaultPayload["referenceCount"] -= 1;
    this.defaultPayloadChange.emit(this.defaultPayload);
  }


  // Coverage count updation

  resetCoverageCountErrors() {
    this.coverageDecrementBtnDisabled = false;
    this.coverageIncrementBtnDisabled = false;
    this.coverageBelowExpectedCountError = false;
    this.coverageAboveExpectedCountError = false;
  }

  updateCoverageCount(event) {

    this.resetCoverageCountErrors();
    let coverageCount: number;
    if(this.requestTemplate){
        coverageCount = parseInt(typeof event === 'string' ? event : event?.target?.value);
    }
    else{
        coverageCount = parseInt(event.target.value);
    }

    if (isNaN(coverageCount)) { //check if reference count is not a number
        if (typeof event !== 'string') {
            event.stopPropagation();
        }
        return;
    }
    if (coverageCount < this.defaultRequestSettings.minCoverageCount) {
        this.coverageBelowExpectedCountError = true;
        this.coverageIncrementBtnDisabled = true;
        this.coverageDecrementBtnDisabled = true;
    } else if (coverageCount > this.defaultRequestSettings.maxCoverageCount) {
        this.coverageAboveExpectedCountError = true;
        this.coverageIncrementBtnDisabled = true;
        this.coverageDecrementBtnDisabled = true;

      } else { //edge case when user enters max/min number
        this.coverageIncrementBtnDisabled = (coverageCount == this.defaultRequestSettings.maxCoverageCount) ? true : false;
        this.coverageDecrementBtnDisabled = (coverageCount == this.defaultRequestSettings.minCoverageCount) ? true : false;

      }
    this.defaultPayload["coverageCount"] = coverageCount;
    this.defaultPayloadChange.emit(this.defaultPayload);
    this.coverageInputError = this.coverageBelowExpectedCountError || this.coverageAboveExpectedCountError;
    this.setError();
  }

  incrementCoverageCount() {
    this.coverageDecrementBtnDisabled = ( this.defaultPayload["coverageCount"]+1 < this.defaultRequestSettings.minCoverageCount) ? true : false;
    this.coverageIncrementBtnDisabled = ( this.defaultPayload["coverageCount"]+1 > this.defaultRequestSettings.maxCoverageCount - 1) ? true : false; //subtracting -1 as it btn needs to be disabled at the max count
    this.defaultPayload["coverageCount"] += 1;
    this.defaultPayloadChange.emit(this.defaultPayload);
  }

  decrementCoverageCount() {
    this.coverageDecrementBtnDisabled = ( this.defaultPayload["coverageCount"]-1 < this.defaultRequestSettings.minCoverageCount + 1) ? true : false; //adding +1 as it btn needs to be disabled at the min count
    this.coverageIncrementBtnDisabled = ( this.defaultPayload["coverageCount"]-1 > this.defaultRequestSettings.maxCoverageCount) ? true : false;
    this.defaultPayload["coverageCount"] -= 1;
    this.defaultPayloadChange.emit(this.defaultPayload);
  }

  //Minimum Coverage CheckBox
  displayMinimumCoverageInputField() {
    this.bsModalRef = this.modalService.show(WarningModalContentComponent, {
      class: 'modal-dialog-centered',
      initialState: {
        "title": $localize`Add minimum coverage period`,
        "message": $localize`By selecting a minimum coverage period, you are enforcing a minimum requirement. Candidates will not be able to complete the process if they are unable to provide references covering the required period. \n\nAre you sure you want to continue?`,
        "agreeButton": $localize`Add coverage`
      }
    });
    this.bsModalRef.content.selection.subscribe(res => {
      if (res === true) {
        this.minimumCoverageClicked = true;
        this.defaultPayload["coverageCountAdded"]=true;
        this.defaultPayloadChange.emit(this.defaultPayload);
      }
    });
  }

  hideMinimumCoverageInputField() {
    this.minimumCoverageClicked = false;
    this.defaultPayload['coverageCountAdded']=false;
    if(this.requestTemplate){
        this.defaultPayload['allowTimeGaps'] = false;
    }
    this.defaultPayloadChange.emit(this.defaultPayload);
    this.resetToDefault();
  }

  resetToDefault() {
    this.defaultPayload['allowTimeGaps'] = false;

    this.coverageBelowExpectedCountError = false;
    this.coverageAboveExpectedCountError = false;

    this.allowTimeGapsWeeksBelowCountError = false;
    this.allowTimeGapsWeeksAboveCountError = false;
    this.allowTimeGapsDaysBelowCountError = false;
    this.allowTimeGapsDaysAboveCountError = false;
  }

  //Allow time gap
  checkAllowTimeGap(event) {
    this.defaultPayload['allowTimeGaps'] = event.target.checked;
    this.defaultPayloadChange.emit(this.defaultPayload);
  }

  resetAllowTimeGapCountErrors() {
    this.allowTimeGapDecrementBtnDisabled = false;
    this.allowTimeGapIncrementBtnDisabled = false;
    this.allowTimeGapsWeeksAboveCountError = false;
    this.allowTimeGapsWeeksBelowCountError = false;
    this.allowTimeGapsDaysAboveCountError = false;
    this.allowTimeGapsDaysBelowCountError = false;
  }

  // Time gap count updation
  updateTimeGapCount(event) {

    this.resetAllowTimeGapCountErrors();
    let timeGap: number;
    if(this.requestTemplate){
        timeGap = parseInt(typeof event === 'string' ? event : event?.target?.value);
    }
    else{
        timeGap = parseInt(event.target.value);
    }

    if (isNaN(timeGap)) {
      if (typeof event !== 'string') {
          event.stopPropagation();
      }
      return;
    } else {
      if (this.defaultPayload["selectedTimeGapPeriod"] == 'Weeks' || this.defaultPayload["selectedTimeGapPeriod"] == 'Week') {
        if (timeGap < this.defaultRequestSettings.minTimeGapWeeksCount) {
          this.allowTimeGapsWeeksBelowCountError = true;
          this.allowTimeGapDecrementBtnDisabled = true;
          this.allowTimeGapIncrementBtnDisabled = true;
          this.defaultPayload["timeGap"] = timeGap;
          this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
        } else if (timeGap > this.defaultRequestSettings.maxTimeGapWeeksCount) {
          this.allowTimeGapsWeeksAboveCountError = true;
          this.allowTimeGapIncrementBtnDisabled = true;
          this.allowTimeGapDecrementBtnDisabled = true;
          this.defaultPayload["timeGap"] = timeGap;
          this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
        } else {
          this.allowTimeGapDecrementBtnDisabled = (timeGap == this.defaultRequestSettings.minTimeGapWeeksCount) ? true : false;
          this.allowTimeGapIncrementBtnDisabled = (timeGap == this.defaultRequestSettings.maxTimeGapWeeksCount) ? true : false;
          this.defaultPayload["timeGap"] = timeGap;
          this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
          this.defaultPayloadChange.emit(this.defaultPayload);
        }
      } else {
        if (timeGap < this.defaultRequestSettings.minTimeGapDaysCount) {
          this.allowTimeGapsDaysBelowCountError = true;
          this.allowTimeGapDecrementBtnDisabled = true;
          this.allowTimeGapIncrementBtnDisabled = true;
          this.defaultPayload["timeGap"] = timeGap;
          this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
        } else if (timeGap > this.defaultRequestSettings.maxTimeGapDaysCount) {
          this.allowTimeGapsDaysAboveCountError = true;
          this.allowTimeGapIncrementBtnDisabled = true;
          this.allowTimeGapDecrementBtnDisabled = true;
          this.defaultPayload["timeGap"] = timeGap;
          this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
        } else {
          this.allowTimeGapDecrementBtnDisabled = (timeGap == this.defaultRequestSettings.minTimeGapDaysCount) ? true : false;
          this.allowTimeGapIncrementBtnDisabled = (timeGap == this.defaultRequestSettings.maxTimeGapDaysCount) ? true : false;
          this.defaultPayload["timeGap"] = timeGap;
          this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Day' : 'Days';

        }
      }
      this.defaultPayloadChange.emit(this.defaultPayload);
      this.allowTimeGapInputError = this.allowTimeGapsWeeksBelowCountError || this.allowTimeGapsWeeksAboveCountError || this.allowTimeGapsDaysBelowCountError || this.allowTimeGapsDaysAboveCountError;
      this.setError();
    }
  }

  incrementTimeGapCount() {
    const timeGap =  this.defaultPayload["timeGap"]+1
    if (this.defaultPayload["selectedTimeGapPeriod"] == 'Weeks' || this.defaultPayload["selectedTimeGapPeriod"] == 'Week') {
      this.allowTimeGapDecrementBtnDisabled = (timeGap < this.defaultRequestSettings.minTimeGapWeeksCount) ? true : false;
      this.allowTimeGapIncrementBtnDisabled = (timeGap > this.defaultRequestSettings.maxTimeGapWeeksCount - 1) ? true : false;
      this.allowTimeGapsWeeksAboveCountError = (timeGap > this.defaultRequestSettings.maxTimeGapWeeksCount) ? true : false;
      this.allowTimeGapsDaysBelowCountError = (timeGap < this.defaultRequestSettings.minTimeGapWeeksCount) ? true : false;
      this.defaultPayload["timeGap"] = timeGap;
      this.defaultPayload["selectedTimeGapPeriod"] =  (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
      this.defaultPayloadChange.emit(this.defaultPayload);

    } else {
      this.allowTimeGapDecrementBtnDisabled = (timeGap < this.defaultRequestSettings.minTimeGapDaysCount) ? true : false;
      this.allowTimeGapIncrementBtnDisabled = (timeGap > this.defaultRequestSettings.maxTimeGapDaysCount - 1) ? true : false;
      this.allowTimeGapsDaysAboveCountError = (timeGap > this.defaultRequestSettings.maxTimeGapDaysCount) ? true : false;
      this.allowTimeGapsDaysBelowCountError = (timeGap < this.defaultRequestSettings.minTimeGapDaysCount) ? true : false;
      this.defaultPayload["timeGap"] = timeGap;
      this.defaultPayload["selectedTimeGapPeriod"] = (timeGap == 0 || timeGap == 1) ? 'Day' : 'Days';
      this.defaultPayloadChange.emit(this.defaultPayload);
    }
    this.allowTimeGapInputError = this.allowTimeGapsWeeksBelowCountError || this.allowTimeGapsWeeksAboveCountError || this.allowTimeGapsDaysBelowCountError || this.allowTimeGapsDaysAboveCountError;
    this.setError();

  }

  decrementTimeGapCount() {
    const timeGap =  this.defaultPayload["timeGap"]-1;
    if (this.defaultPayload["selectedTimeGapPeriod"]== 'Weeks' || this.defaultPayload["selectedTimeGapPeriod"] == 'Week') {
      this.allowTimeGapIncrementBtnDisabled = (timeGap > this.defaultRequestSettings.maxTimeGapWeeksCount - 1) ? true : false;
      this.allowTimeGapDecrementBtnDisabled = (timeGap < this.defaultRequestSettings.minTimeGapWeeksCount + 1) ? true : false;
      this.allowTimeGapsWeeksAboveCountError = (timeGap > this.defaultRequestSettings.maxTimeGapWeeksCount) ? true : false;
      this.allowTimeGapsDaysBelowCountError = (timeGap < this.defaultRequestSettings.minTimeGapWeeksCount) ? true : false;
      this.defaultPayload["timeGap"] = timeGap;
      this.defaultPayload["selectedTimeGapPeriod"] =  (timeGap == 0 || timeGap == 1) ? 'Week' : 'Weeks';
      this.defaultPayloadChange.emit(this.defaultPayload);
    } else {
      this.allowTimeGapIncrementBtnDisabled = (timeGap > this.defaultRequestSettings.maxTimeGapDaysCount - 1) ? true : false;
      this.allowTimeGapDecrementBtnDisabled = (timeGap < this.defaultRequestSettings.minTimeGapDaysCount + 1) ? true : false;
      this.allowTimeGapsDaysAboveCountError = (timeGap > this.defaultRequestSettings.maxTimeGapDaysCount) ? true : false;
      this.allowTimeGapsDaysBelowCountError = (timeGap < this.defaultRequestSettings.minTimeGapDaysCount) ? true : false;
      this.defaultPayload["timeGap"] = timeGap;
      this.defaultPayload["selectedTimeGapPeriod"] =  (timeGap == 0 || timeGap == 1) ? 'Day' : 'Days';
      this.defaultPayloadChange.emit(this.defaultPayload);
    }
    this.allowTimeGapInputError = this.allowTimeGapsWeeksBelowCountError || this.allowTimeGapsWeeksAboveCountError || this.allowTimeGapsDaysBelowCountError || this.allowTimeGapsDaysAboveCountError;
    this.setError();

  }

  selectTimeGapPeriod(timePeriod) {
    this.resetAllowTimeGapCountErrors();
    if (timePeriod == 'Days' || timePeriod == 'Day') {
      this.allowTimeGapsDaysAboveCountError = ( this.defaultPayload["timeGap"] > this.defaultRequestSettings.maxTimeGapDaysCount) ? true : false;
      this.allowTimeGapsDaysBelowCountError = ( this.defaultPayload["timeGap"] < this.defaultRequestSettings.minTimeGapDaysCount) ? true : false;

      //disable both increment and decrement btn when max / min value reached
      this.allowTimeGapIncrementBtnDisabled = ( this.defaultPayload["timeGap"] > this.defaultRequestSettings.maxTimeGapDaysCount - 1 ||  this.defaultPayload["timeGap"] < this.defaultRequestSettings.minTimeGapDaysCount) ? true : false;
      this.allowTimeGapDecrementBtnDisabled = ( this.defaultPayload["timeGap"] > this.defaultRequestSettings.maxTimeGapDaysCount ||  this.defaultPayload["timeGap"] < this.defaultRequestSettings.minTimeGapDaysCount + 1) ? true : false;
    } else {
      this.allowTimeGapsWeeksAboveCountError = ( this.defaultPayload["timeGap"] > this.defaultRequestSettings.maxTimeGapWeeksCount) ? true : false;
      this.allowTimeGapsWeeksBelowCountError = ( this.defaultPayload["timeGap"]< this.defaultRequestSettings.minTimeGapWeeksCount) ? true : false;

      //disable both increment and decrement btn when max / min value reached
      this.allowTimeGapIncrementBtnDisabled = ( this.defaultPayload["timeGap"] > this.defaultRequestSettings.maxTimeGapWeeksCount - 1 || this.defaultPayload["timeGap"]< this.defaultRequestSettings.minTimeGapWeeksCount) ? true : false;
      this.allowTimeGapDecrementBtnDisabled = ( this.defaultPayload["timeGap"]> this.defaultRequestSettings.maxTimeGapWeeksCount ||  this.defaultPayload["timeGap"]< this.defaultRequestSettings.minTimeGapWeeksCount + 1) ? true : false;

    }
    this.allowTimeGapInputError = this.allowTimeGapsWeeksBelowCountError || this.allowTimeGapsWeeksAboveCountError || this.allowTimeGapsDaysBelowCountError || this.allowTimeGapsDaysAboveCountError;
    this.setError();
    if(!this.allowTimeGapInputError){
      this.defaultPayload["selectedTimeGapPeriod"] = timePeriod;
      this.defaultPayloadChange.emit(this.defaultPayload);
    }
  }

  changeNoteToCandidateValue(updatedNotetoCandidate) {
    this.defaultPayload["noteToCandidate"]=updatedNotetoCandidate;
    this.defaultPayloadChange.emit(this.defaultPayload);
  }

  changeNoteToRefereeValue(updatedNotetoReferee) {
    this.defaultPayload["noteToReferee"]= updatedNotetoReferee;
    this.defaultPayloadChange.emit(this.defaultPayload);
  }

  revertToDefaultCandidateNote() {
    this.bsModalRef = this.modalService.show(WarningModalContentComponent, {
      class: 'modal-dialog-centered',
      initialState: {
        "title": $localize`Revert to default?`,
        "message": $localize`By reverting to the default, all changes will be lost.\n\nAre you sure you want to continue?`
      }
    });
    this.bsModalRef.content.selection.subscribe(res => {
      if (res === true) {
        this.defaultPayload["noteToCandidate"]= this.defaultNotes.noteToCandidate;
        this.defaultPayloadChange.emit(this.defaultPayload);
      }
    });
  }

  revertToDefaultRefereeNote() {
    this.bsModalRef = this.modalService.show(WarningModalContentComponent, {
      class: 'modal-dialog-centered',
      initialState: {
        "title": $localize`Change request type?`,
        "message": $localize`By reverting to the default, all changes will be lost.\n\nAre you sure you want to continue?`
      }
    });
    this.bsModalRef.content.selection.subscribe(res => {
      if (res === true) {
        this.defaultPayload["noteToReferee"]=this.defaultNotes.noteToReferee;
        this.defaultPayloadChange.emit(this.defaultPayload);
      }
    });
  }

  getHiringAccounts() {
    this.requestService.listHiringAccounts().subscribe(res => {
      res['hiring_accounts'].forEach(val => this.hiringAccounts.push(val));
    })
  }

  changeHiringAccount(hiringAccount) {
    this.selectedHiringAccount = hiringAccount['name'];
    this.defaultPayload['hiringAccount'] = hiringAccount['id'];
    this.defaultPayloadChange.emit(this.defaultPayload);
  }
}
